import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

function GoogleG() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l-.02.15 3.5 2.7.24.03c2.2-2 3.5-5 3.5-8.6z" />
      <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.8-2.9c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5l-.14.01-3.6 2.8-.05.13C3.5 21.3 7.4 24 12 24z" />
      <path fill="#FBBC05" d="M5.3 14.4c-.25-.75-.4-1.55-.4-2.4s.15-1.65.42-2.4l-.01-.16L1.65 6.6l-.12.06C.56 8.2 0 10 0 12s.56 3.8 1.53 5.34l3.77-2.94z" />
      <path fill="#EB4335" d="M12 4.6c2.2 0 3.7.95 4.6 1.75l3.35-3.3C17.9 1.05 15.2 0 12 0 7.4 0 3.5 2.7 1.53 6.66l3.78 2.94C6.2 6.7 8.9 4.6 12 4.6z" />
    </svg>
  );
}

const tabCls = (active) =>
  `pb-3 px-1 font-mono text-[11px] tracking-[0.2em] uppercase border-b-2 transition-colors ${
    active ? "border-gold text-gold" : "border-transparent text-white/40 hover:text-white"
  }`;

export default function AuthModal() {
  const { authModal, openAuth, login, register, googleSignIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const isLogin = authModal === "login";
  const close = () => {
    openAuth(null);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (isLogin) {
        await login(email, password);
        toast.success("Willkommen zurück im Ring.");
      } else {
        await register(email, password, name);
        toast.success("Willkommen bei MAZARO — Bestätigungs-E-Mail ist unterwegs.");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {authModal && (
        <motion.div
          key="auth-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
          data-testid="auth-modal"
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md border border-white/10 bg-panel p-8"
          >
            <button data-testid="auth-modal-close" onClick={close} className="absolute top-4 right-4 text-white/50 hover:text-white" aria-label="Schließen">
              <X size={20} />
            </button>

            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold mb-3">MAZARO Ring Access</p>
            <div className="flex gap-6 border-b border-white/10 mb-6">
              <button data-testid="auth-login-tab" onClick={() => openAuth("login")} className={tabCls(isLogin)}>
                Anmelden
              </button>
              <button data-testid="auth-register-tab" onClick={() => openAuth("register")} className={tabCls(!isLogin)}>
                Registrieren
              </button>
            </div>

            <form onSubmit={submit} className="space-y-4">
              {!isLogin && (
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                  data-testid="auth-name-input"
                  className="w-full bg-black/40 border border-white/15 px-5 py-4 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-gold"
                />
              )}
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="E-Mail"
                data-testid="auth-email-input"
                className="w-full bg-black/40 border border-white/15 px-5 py-4 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-gold"
              />
              <input
                type="password"
                required
                minLength={isLogin ? 1 : 8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isLogin ? "Passwort" : "Passwort (min. 8 Zeichen)"}
                data-testid="auth-password-input"
                className="w-full bg-black/40 border border-white/15 px-5 py-4 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-gold"
              />
              {error && (
                <p data-testid="auth-error" className="text-red-400 text-sm">
                  {error}
                </p>
              )}
              <button type="submit" disabled={busy} data-testid="auth-submit-button" className="btn-gold w-full justify-center disabled:opacity-60 disabled:hover:transform-none">
                {busy ? "Bitte warten…" : isLogin ? "Anmelden" : "Account erstellen"}
              </button>
            </form>

            <div className="my-6 flex items-center gap-3">
              <span className="h-px flex-1 bg-white/10" />
              <span className="font-mono text-[10px] text-white/30 tracking-[0.2em]">ODER</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>

            <button data-testid="auth-google-button" onClick={googleSignIn} className="btn-ghost w-full justify-center">
              <GoogleG /> Mit Google anmelden
            </button>

            <p className="mt-6 font-mono text-[10px] tracking-[0.2em] uppercase text-white/30 text-center">Born to Fight — Kein Spam</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
